import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import { createSimulation } from '../../../utils/simulationApi';
import {
  getUseCaseFromTask,
  transformContentDataToFormData,
  getUseCaseDisplayName,
  UseCaseType,
  ContentData,
} from '../../../utils/taskToUseCaseMap';

// Import all use case forms
import PackagingReviewV2Form from '../../simulationUseCase/useCaseForms/PackagingReviewV2Form';
import SurveyAndFocusGroups from '../../simulationUseCase/useCaseForms/SurveyAndFocusGroups';
import LogoTest from '../../simulationUseCase/useCaseForms/LogoTest';
import ABTestCreatives from '../../simulationUseCase/useCaseForms/ABTestCreatives';
import ImageSurvey from '../../simulationUseCase/useCaseForms/ImageSurvey';
import ABTestCreativesGemini from '../../simulationUseCase/useCaseForms/ABTestCreativesGemini';
import ABTestCreativePackagingForm from '../../simulationUseCase/useCaseForms/ABTestCreativePackaging';
import ContentCreationForm from '../../simulationUseCase/useCaseForms/ContentCreationForm';
import ContentTestingForm from '../../simulationUseCase/useCaseForms/ContentTestingForm';
import InsightsForm from '../../simulationUseCase/useCaseForms/InsightsForm';
import PricingForm from '../../simulationUseCase/useCaseForms/PricingForm';
import AttributionForm from '../../simulationUseCase/useCaseForms/AttributionForm';
import BuyerInsightsForm from '../../simulationUseCase/useCaseForms/BuyerInsightsForm';
import CreateCampaignStratergy from '../../simulationUseCase/useCaseForms/CreateCampaignStratergy';
import TestUseCase from '../../simulationUseCase/useCaseForms/TestUseCase';
import ConceptTestForm from '../../simulationUseCase/useCaseForms/ConceptTestForm';
import ClaimsTestingForm from '../../simulationUseCase/useCaseForms/ClaimsTesting';
import DrivingConceptTestForm from '../../simulationUseCase/useCaseForms/DrivingConceptTestForm';
import VitaminConceptTestForm from '../../simulationUseCase/useCaseForms/VitaminConceptTestForm';
import PackagingVariantComparisonForm from '../../simulationUseCase/useCaseForms/PackagingVariantComparisonForm';

interface SimulationData {
  id: number;
  audience_id: number;
  source_model: string;
  created_at: string;
  personas: Array<{
    id: number;
    name: string;
    data: any;
  }>;
  segments?: Array<{
    id: number;
    name: string;
  }>;
}

interface EditRerunModalProps {
  isOpen: boolean;
  onClose: () => void;
  simulation: SimulationData;
  contentData: ContentData;
}

const EditRerunModal: React.FC<EditRerunModalProps> = ({
  isOpen,
  onClose,
  simulation,
  contentData,
}) => {
  const navigate = useNavigate();
  const { audienceData, updateAudienceData } = useAudience();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get use case type from task
  const useCaseType = useMemo(() => {
    return getUseCaseFromTask(contentData?.task);
  }, [contentData?.task]);

  // Transform content data to form data
  const initialFormData = useMemo(() => {
    if (!contentData || !useCaseType) return {};
    const formData = transformContentDataToFormData(contentData, useCaseType);
    // Debug logging to help diagnose data transformation issues
    console.log('[EditRerunModal] useCaseType:', useCaseType);
    console.log('[EditRerunModal] contentData:', contentData);
    console.log('[EditRerunModal] transformed initialFormData:', formData);
    return formData;
  }, [contentData, useCaseType]);

  // Extract segment IDs and persona filters from contentData
  const selectedSegmentIds = useMemo(() => {
    return contentData?.segment_ids || [];
  }, [contentData?.segment_ids]);

  const personaFilters = useMemo(() => {
    const filters = contentData?.persona_filters;
    if (!filters || typeof filters !== 'object') return {};
    // Convert to expected format: Record<number, SegmentPersonaFilters>
    const result: Record<number, any> = {};
    Object.entries(filters).forEach(([key, value]) => {
      const numKey = parseInt(key, 10);
      if (!isNaN(numKey)) {
        result[numKey] = value;
      }
    });
    return result;
  }, [contentData?.persona_filters]);

  // Extract dynamic segmentation data
  const dynamicSegmentationId = useMemo(() => {
    return contentData?.dynamic_segmentation_id || null;
  }, [contentData?.dynamic_segmentation_id]);

  const dynamicSegmentIndices = useMemo(() => {
    return contentData?.dynamic_segment_indices || [];
  }, [contentData?.dynamic_segment_indices]);

  // Set audience data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (simulation?.audience_id) {
        updateAudienceData({
          audienceId: simulation.audience_id,
          audienceName: contentData?.audience_name || '',
        });
      }
      setIsLoading(false);
    }
  }, [isOpen, simulation?.audience_id, contentData?.audience_name, updateAudienceData]);

  // Handle form submission - navigate to new simulation results
  const handleFormSubmit = (newSimulationId: number) => {
    onClose();
    navigate(`/simulation-results/${newSimulationId}`);
  };

  // Handle driving concept test payload - calls API then navigates
  const handleDrivingSavePayload = useCallback(async (payload: Record<string, any>) => {
    try {
      setError(null);
      const result = await createSimulation(payload);
      if (result.simulation_id) {
        onClose();
        navigate(`/simulation-results/${result.simulation_id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start simulation');
    }
  }, [navigate, onClose]);

  // Handle back button - just close modal
  const handleBack = () => {
    onClose();
  };

  // Render the appropriate form based on use case type
  const renderForm = () => {
    if (!useCaseType) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">
            This simulation type cannot be edited and rerun.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Task type: {contentData?.task || 'Unknown'}
          </p>
        </div>
      );
    }

    const commonProps = {
      onSubmit: handleFormSubmit,
      selectedSegmentIds,
      personaFilters,
      onBack: handleBack,
      initialFormData,
      onFormDataChange: () => {}, // Not tracking form changes in edit mode
      // Dynamic segmentation support
      dynamicSegmentationId,
      dynamicSegmentIndices,
    };

    switch (useCaseType) {
      case 'packaging-review-v2':
        return <PackagingReviewV2Form {...commonProps} />;

      case 'packaging-review-gemini':
        return <ABTestCreativePackagingForm {...commonProps} />;

      case 'survey-and-focus-groups':
        return <SurveyAndFocusGroups {...commonProps} />;

      case 'logo_test':
        return <LogoTest {...commonProps} />;

      case 'channel-strategy':
        return <ABTestCreatives {...commonProps} />;

      case 'image-survey':
        return <ImageSurvey {...commonProps} />;

      case 'ab-test-creatives-gemini':
        return <ABTestCreativesGemini {...commonProps} />;

      case 'create-content':
        return <ContentCreationForm {...commonProps} />;

      case 'content-testing':
        return <ContentTestingForm {...commonProps} />;

      case 'get-insights':
        return <InsightsForm {...commonProps} />;

      case 'pricing-analysis':
        return <PricingForm {...commonProps} />;

      case 'attribution':
        return <AttributionForm {...commonProps} />;

      case 'buyer-insights-report':
        return <BuyerInsightsForm {...commonProps} />;

      case 'create-campaign-strategy':
        return <CreateCampaignStratergy {...commonProps} />;

      case 'test-use-case':
        return <TestUseCase {...commonProps} />;

      case 'concept-test':
        return <ConceptTestForm {...commonProps} />;

      case 'claims-testing':
        return <ClaimsTestingForm {...commonProps} />;

      case 'driving-concept-test':
        return (
          <DrivingConceptTestForm
            {...commonProps}
            onSavePayload={handleDrivingSavePayload}
          />
        );

      case 'vitamin-concept-test':
        return (
          <VitaminConceptTestForm
            {...commonProps}
            onSavePayload={handleDrivingSavePayload}
          />
        );

      case 'packaging-variant-comparison':
        return <PackagingVariantComparisonForm {...commonProps} />;

      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-500">
              Form not available for this simulation type.
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Use case: {useCaseType}
            </p>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full h-full max-w-6xl max-h-[95vh] m-4 bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Edit & Rerun Simulation
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {useCaseType ? getUseCaseDisplayName(useCaseType) : 'Unknown Type'}
                {contentData?.name && (
                  <span className="ml-2 text-gray-500">
                    • {contentData.name}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading simulation data...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="mx-auto max-w-4xl">
              {renderForm()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditRerunModal;

